import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search, RefreshCw, AlertTriangle, X, Eye, CheckCircle, 
  XCircle, Clock, User, Building2, Calendar, FileText,
  CalendarClock, ShieldAlert, Star, ListChecks
} from 'lucide-react';

const STATUS_CONFIG = {
  aberta: { 
    label: 'Aberta', 
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Clock
  },
  em_analise: { 
    label: 'Em Análise', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Eye
  },
  resolvida_cliente: { 
    label: 'Favor Cliente', 
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle
  },
  resolvida_prestador: { 
    label: 'Favor Prestador', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Building2
  }
};

export default function AdminDisputes() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [selectedDispute, setSelectedDispute] = useState(null);
  // Contexto operacional (Trust & Safety) carregado quando o modal abre
  const [disputeContext, setDisputeContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchDisputes();
  }, [user, navigate]);

  // PROMPT BLINDADO: Refetch quando filtro de status muda
  useEffect(() => {
    if (user && user.user_type === 'admin') {
      fetchDisputes(true);
    }
  }, [filters.status]);

  // Carregar contexto operacional (histórico da conta) quando um modal abre
  useEffect(() => {
    if (!selectedDispute?.id) {
      setDisputeContext(null);
      setContextLoading(false);
      return;
    }
    let cancelled = false;
    const loadContext = async () => {
      setContextLoading(true);
      setDisputeContext(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/disputes/admin/${selectedDispute.id}/context`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setDisputeContext(res.data);
      } catch (e) {
        if (!cancelled) setDisputeContext(null);
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    };
    loadContext();
    return () => { cancelled = true; };
  }, [selectedDispute?.id]);

  const formatClientSince = (isoStr) => {
    if (!isoStr) return 'N/A';
    try {
      const d = new Date(isoStr);
      if (Number.isNaN(d.getTime())) return 'N/A';
      const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
      return `${months[d.getMonth()]}/${d.getFullYear()}`;
    } catch {
      return 'N/A';
    }
  };

  const fetchDisputes = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const res = await axios.get(`${API}/disputes/admin/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisputes(res.data.disputes || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar contestações');
      setDisputes([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleStartAnalysis = async (disputeId, e) => {
    e?.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/disputes/admin/${disputeId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contestação marcada como em análise');
      fetchDisputes(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar análise');
    }
  };

  const handleResolve = async (decision) => {
    if (!selectedDispute) return;
    setResolving(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/disputes/admin/${selectedDispute.id}/resolve`, 
        { decision },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        decision === 'cliente' 
          ? 'Contestação resolvida a favor do cliente' 
          : 'Contestação resolvida a favor do prestador'
      );
      setSelectedDispute(null);
      fetchDisputes(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resolver contestação');
    } finally {
      setResolving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDisputes = disputes.filter(dispute => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        dispute.client?.name?.toLowerCase().includes(search) ||
        dispute.provider?.name?.toLowerCase().includes(search) ||
        dispute.provider?.business_name?.toLowerCase().includes(search) ||
        dispute.motivo?.toLowerCase().includes(search) ||
        dispute.descricao?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ search: '', status: '' });
  const hasFilters = filters.search || filters.status;

  // Contagem por status
  const statusCounts = {
    aberta: disputes.filter(d => d.status === 'aberta').length,
    em_analise: disputes.filter(d => d.status === 'em_analise').length,
    resolvida_cliente: disputes.filter(d => d.status === 'resolvida_cliente').length,
    resolvida_prestador: disputes.filter(d => d.status === 'resolvida_prestador').length,
  };

  if (loading) {
    return (
      <AdminLayout title="Contestações" subtitle="Carregando...">
        <div className="space-y-6">
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="disputes-title">Contestações</h1>
            <p className="text-slate-500">Gerenciamento de disputas de serviços</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchDisputes(true)} disabled={refreshing} data-testid="refresh-disputes-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border-l-4 border-l-yellow-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusCounts.aberta}</p>
                <p className="text-xs text-slate-500">Abertas</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusCounts.em_analise}</p>
                <p className="text-xs text-slate-500">Em Análise</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusCounts.resolvida_cliente}</p>
                <p className="text-xs text-slate-500">Favor Cliente</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-purple-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusCounts.resolvida_prestador}</p>
                <p className="text-xs text-slate-500">Favor Prestador</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por cliente, prestador ou motivo..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                data-testid="search-disputes-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <Button
                  key={key}
                  variant={filters.status === key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setFilters({ ...filters, status: filters.status === key ? '' : key });
                    fetchDisputes(true);
                  }}
                  data-testid={`filter-${key}-btn`}
                >
                  {config.label}
                </Button>
              ))}
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Disputes List */}
        <div className="space-y-3">
          {filteredDisputes.length > 0 ? (
            filteredDisputes.map((dispute) => {
              const statusConfig = STATUS_CONFIG[dispute.status] || STATUS_CONFIG.aberta;
              const StatusIcon = statusConfig.icon;
              const canResolve = ['aberta', 'em_analise'].includes(dispute.status);
              
              return (
                <Card 
                  key={dispute.id} 
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedDispute(dispute)}
                  data-testid={`dispute-card-${dispute.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Status e ID */}
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${statusConfig.color.replace('text-', 'bg-').split(' ')[0]}`}>
                        <StatusIcon className={`w-5 h-5 ${statusConfig.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">#{dispute.id}</p>
                      </div>
                    </div>

                    {/* Partes envolvidas */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                            {getInitials(dispute.client?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{dispute.client?.name || 'Cliente'}</p>
                          <p className="text-xs text-slate-400">Cliente</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-green-100 text-green-700 text-xs">
                            {getInitials(dispute.provider?.business_name || dispute.provider?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {dispute.provider?.business_name || dispute.provider?.name || 'Prestador'}
                          </p>
                          <p className="text-xs text-slate-400">Prestador</p>
                        </div>
                      </div>
                    </div>

                    {/* Motivo */}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{dispute.motivo}</p>
                      <p className="text-xs text-slate-400 truncate">{dispute.descricao}</p>
                    </div>

                    {/* Data e Ações */}
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{formatDate(dispute.created_at)}</p>
                        {dispute.appointment && (
                          <p className="text-xs text-slate-500">
                            Serviço: {dispute.appointment.date}
                          </p>
                        )}
                      </div>
                      {canResolve && dispute.status === 'aberta' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => handleStartAnalysis(dispute.id, e)}
                          data-testid={`analyze-btn-${dispute.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Analisar
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma contestação encontrada</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'As contestações de serviços aparecerão aqui'}
              </p>
            </Card>
          )}
        </div>

        {/* Summary */}
        {filteredDisputes.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {filteredDisputes.length} de {disputes.length} contestações
          </p>
        )}
      </div>

      {/* Modal de Detalhes e Resolução */}
      <Dialog open={!!selectedDispute} onOpenChange={() => setSelectedDispute(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Detalhes da Contestação
            </DialogTitle>
            <DialogDescription>
              Analise as informações e tome uma decisão
            </DialogDescription>
          </DialogHeader>

          {selectedDispute && (
            <div className="space-y-6 py-4">
              {/* Status atual */}
              <div className="flex items-center justify-between">
                <Badge className={STATUS_CONFIG[selectedDispute.status]?.color || 'bg-gray-100'}>
                  {STATUS_CONFIG[selectedDispute.status]?.label || selectedDispute.status}
                </Badge>
                <span className="text-sm text-slate-500">ID: #{selectedDispute.id}</span>
              </div>

              {/* Partes */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-slate-500">Cliente</span>
                  </div>
                  <p className="font-semibold">{selectedDispute.client?.name || 'N/A'}</p>
                  <p className="text-sm text-slate-500">{selectedDispute.client?.email}</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-slate-500">Prestador</span>
                  </div>
                  <p className="font-semibold">
                    {selectedDispute.provider?.business_name || selectedDispute.provider?.name || 'N/A'}
                  </p>
                </Card>
              </div>

              {/* Histórico da Conta — contexto operacional (Trust & Safety) */}
              <Card className="p-4 border border-slate-200" data-testid="dispute-account-history">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Histórico da Conta</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CalendarClock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Cliente desde</p>
                      <p className="font-semibold text-slate-700" data-testid="ctx-client-since">
                        {contextLoading ? '…' : formatClientSince(disputeContext?.client_since)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Contestações anteriores</p>
                      <p
                        className={`font-semibold ${
                          (disputeContext?.client_previous_disputes || 0) > 0
                            ? 'text-amber-700'
                            : 'text-slate-700'
                        }`}
                        data-testid="ctx-prev-disputes"
                      >
                        {contextLoading ? '…' : (disputeContext?.client_previous_disputes ?? 0)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Score prestador</p>
                      <p className="font-semibold text-slate-700" data-testid="ctx-provider-score">
                        {contextLoading
                          ? '…'
                          : disputeContext?.provider_score != null
                            ? Number(disputeContext.provider_score).toFixed(1)
                            : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ListChecks className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Serviços concluídos</p>
                      <p className="font-semibold text-slate-700" data-testid="ctx-provider-completed">
                        {contextLoading ? '…' : (disputeContext?.provider_completed_services ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Dados do Agendamento */}
              {selectedDispute.appointment && (
                <Card className="p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Dados do Serviço</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Data:</span> {selectedDispute.appointment.date}</p>
                    <p><span className="text-slate-500">Horário:</span> {selectedDispute.appointment.time}</p>
                    <p><span className="text-slate-500">Tipo:</span> {selectedDispute.appointment.service_type}</p>
                    <p><span className="text-slate-500">Status:</span> {selectedDispute.appointment.status}</p>
                  </div>
                </Card>
              )}

              {/* Motivo e Descrição */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium">Motivo da Contestação</span>
                </div>
                <p className="font-semibold text-slate-800 mb-2">{selectedDispute.motivo}</p>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                  "{selectedDispute.descricao}"
                </p>
              </Card>

              {/* Datas */}
              <div className="flex justify-between text-sm text-slate-500">
                <span>Criada em: {formatDate(selectedDispute.created_at)}</span>
                {selectedDispute.resolved_at && (
                  <span>Resolvida em: {formatDate(selectedDispute.resolved_at)}</span>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedDispute(null)}>
              Fechar
            </Button>
            
            {selectedDispute && ['aberta', 'em_analise'].includes(selectedDispute.status) && (
              <>
                {selectedDispute.status === 'aberta' && (
                  <Button 
                    variant="outline"
                    onClick={() => handleStartAnalysis(selectedDispute.id)}
                    disabled={resolving}
                    data-testid="modal-analyze-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Iniciar Análise
                  </Button>
                )}
                <Button 
                  variant="destructive"
                  onClick={() => handleResolve('cliente')}
                  disabled={resolving}
                  data-testid="resolve-client-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {resolving ? 'Processando...' : 'Favor Cliente'}
                </Button>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => handleResolve('prestador')}
                  disabled={resolving}
                  data-testid="resolve-provider-btn"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  {resolving ? 'Processando...' : 'Favor Prestador'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
